import { Bot, Box, CircleDashed, Lock, MapPin, Mountain, PersonStanding, TreePine, UserPen, type LucideIcon } from 'lucide-react'

import type { EntityType, Origin, RoleSpec } from '@/api/actions'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { cn } from '@/components/ui/utils'

import { describeRole, roleTypes, TYPE_LABEL } from './describe'

/**
 * Small shared pieces of the Actions page: the participant-type chips, the origin badge and the
 * "use it in stories" switch. One look everywhere (card, detail, create-with-AI review).
 */

export const TYPE_ICON: Record<EntityType, LucideIcon> = {
  character: PersonStanding,
  object: Box,
  scenery: TreePine,
  feature: Mountain,
  spot: MapPin,
}

export const ORIGIN_LABEL: Record<Origin, string> = { builtin: 'Built-in', ai: 'Made by AI', user: 'Mine' }
const ORIGIN_TONE: Record<Origin, BadgeTone> = { builtin: 'neutral', ai: 'info', user: 'accent' }
const ORIGIN_ICON: Record<Origin, LucideIcon> = { builtin: Lock, ai: Bot, user: UserPen }

export function OriginBadge({ origin, className }: { origin: Origin; className?: string }) {
  const Icon = ORIGIN_ICON[origin]
  return (
    <Badge tone={ORIGIN_TONE[origin]} className={className} data-testid="origin-badge" data-origin={origin}>
      <Icon className="size-3" aria-hidden />
      {ORIGIN_LABEL[origin]}
    </Badge>
  )
}

/**
 * One chip per participant: the icon(s) of the entity types it accepts, then its name. The title
 * (and the screen-reader text) carries the plain-words description.
 */
export function RoleChip({ name, spec, className }: { name: string; spec: RoleSpec | null | undefined; className?: string }) {
  const types = roleTypes(name, spec)
  const summary = describeRole(name, spec)
  const typeWords = types.length ? types.map((t) => TYPE_LABEL[t].toLowerCase()).join(' or ') : 'anything'
  return (
    <span
      className={cn(
        'inline-flex h-7 min-w-0 max-w-full items-center gap-1 rounded-full border border-line bg-paper px-2 text-xs font-medium text-ink-2',
        !summary.required && 'border-dashed',
        className,
      )}
      title={`${summary.title}: ${summary.sentence}`}
      data-testid="role-chip"
      data-role={name}
      data-types={types.join(' ')}
    >
      <span className="inline-flex shrink-0 items-center gap-0.5 text-ink-3" aria-hidden>
        {types.length === 0 ? (
          <CircleDashed className="size-3.5" />
        ) : (
          types.map((t) => {
            const Icon = TYPE_ICON[t]
            return <Icon key={t} className="size-3.5" />
          })
        )}
      </span>
      <span className="truncate">{summary.title}</span>
      <span className="sr-only">
        {' '}
        ({typeWords}
        {summary.required ? '' : ', optional'})
      </span>
    </span>
  )
}

export function RoleChips({ roles, className, max = 6 }: { roles: Record<string, RoleSpec | null | undefined>; className?: string; max?: number }) {
  const entries = Object.entries(roles ?? {})
  if (entries.length === 0) return null
  return (
    <div className={cn('flex min-w-0 flex-wrap gap-1', className)} aria-label="Who takes part">
      {entries.slice(0, max).map(([name, spec]) => (
        <RoleChip key={name} name={name} spec={spec} />
      ))}
      {entries.length > max ? <span className="self-center text-xs text-ink-3">+{entries.length - max}</span> : null}
    </div>
  )
}

/** The legend for the chips: which icon is which participant type. */
export function TypeLegend({ className }: { className?: string }) {
  return (
    <ul className={cn('flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-3', className)} aria-label="Kinds of participants">
      {(Object.keys(TYPE_ICON) as EntityType[]).map((t) => {
        const Icon = TYPE_ICON[t]
        return (
          <li key={t} className="inline-flex items-center gap-1">
            <Icon className="size-3.5" aria-hidden />
            {TYPE_LABEL[t]}
          </li>
        )
      })}
    </ul>
  )
}

/** An accessible on/off switch (the same look as the wizard's "Living background"). */
export function EnabledSwitch({
  checked,
  onChange,
  label,
  disabled,
  className,
}: {
  checked: boolean
  onChange(next: boolean): void
  label: string
  disabled?: boolean
  className?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={checked ? 'Used in stories' : 'Not used in stories'}
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onChange(!checked)
      }}
      className={cn(
        'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus-visible:focus-ring disabled:opacity-50',
        checked ? 'bg-accent' : 'bg-line-strong',
        className,
      )}
    >
      <span className={cn('inline-block size-5 rounded-full bg-white shadow-card transition-transform', checked ? 'translate-x-6' : 'translate-x-1')} aria-hidden />
    </button>
  )
}
